import { forwardRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Input, { inputClasses, InputProps } from '../Input';
import { INPUT_BORDER_RADIUS } from '../theme/constants';
import colors from '../theme/colors';
import palette from '../theme/palette';
import IconButton from '../IconButton';
import Plus from '@o2/icons/Plus';
import Minus from '@o2/icons/Minus';
import { useNumberInputUtilityClasses } from './numberInputClasses';
import { clsx } from 'clsx';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';

const StyledIconButton = styled(IconButton, {
  name: 'O2NumberInput',
  slot: 'Icon',
})({
  margin: 0,
  padding: '11px 13px',
  borderRadius: '5px',
  border: 'none',
  color: palette.text.secondary,
  '&:first-of-type': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  '&:last-of-type': {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  '&:hover': {
    backgroundColor: colors.brandPrimary[100],
  },
});

const StyledNumberInput = styled('div', {
  name: 'O2NumberInput',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'error' && prop !== 'disabled',
})<InputProps>(({ error, disabled }) => ({
  display: 'inline-flex',
  borderRadius: INPUT_BORDER_RADIUS,
  border: `1px solid ${palette.divider}`,
  background: palette.common.white,
  ...(error && {
    borderColor: palette.error.main,
  }),
  ...(disabled && {
    borderColor: colors.transparentGray[100],
  }),
}));

const StyledInput = styled(Input, {
  name: 'O2NumberInput',
  slot: 'Input',
})({
  minWidth: '50px',
  width: '50px',
  /* Hide spinner for Webkit browsers */
  [`& .${inputClasses.input}::-webkit-inner-spin-button,
    & .${inputClasses.input}::-webkit-outer-spin-button`]: {
    appearance: 'none',
    margin: 0,
  },
  [`& .${inputClasses.input}`]: {
    textAlign: 'center',
    /* Hide spinner for Firefox */
    appearance: 'textfield',
  },
});

const NumberInput = forwardRef<HTMLDivElement, InputProps>((props, ref) => {
  const { disabled, placeholder, defaultValue, inputProps, ...others } = props;
  const defaultVal = (defaultValue as string) || '0';
  const min = inputProps?.min || defaultVal;
  const max = inputProps?.max || undefined;
  const [count, setCount] = useState(defaultVal || undefined);
  const step = inputProps?.step ?? 1;
  const utilityClasses = useNumberInputUtilityClasses();

  return (
    <StyledNumberInput error={props.error} ref={ref} className={clsx(utilityClasses.root)} disabled={props.disabled}>
      <StyledIconButton
        variant="squared"
        color="secondary"
        size="small"
        disabled={disabled || Number(count) <= min}
        onClick={() => setCount((prev) => String(Number(prev) - step))}
        className={clsx(utilityClasses.decreaseIcon)}
      >
        <Minus fontSize="small" />
      </StyledIconButton>
      <StyledInput
        type="number"
        value={count}
        placeholder={placeholder}
        disableUnderline
        disabled={disabled}
        className={clsx(utilityClasses.input)}
        inputProps={{
          max: max,
          min: min,
          step: step,
          placeholder: placeholder,
          ...props?.inputProps,
        }}
        {...others}
        onChange={(event) => {
          setCount(event.target.value);
          props?.onChange && props.onChange(event);
        }}
      />
      <StyledIconButton
        variant="squared"
        color="secondary"
        size="small"
        disabled={disabled || (max ? Number(count) >= max : false)}
        onClick={() => setCount((prev) => String(Number(prev) + step))}
        className={clsx(utilityClasses.increaseIcon)}
      >
        <Plus />
      </StyledIconButton>
    </StyledNumberInput>
  );
});

NumberInput.displayName = 'NumberInput';

export default NumberInput;
